<template>
  <div class="col-md-12 billing-beneficiary-manager">
    <div v-loading="loading">
      <beneficiary-info
        v-for="(item, index) in items"
        :key="index"
        :id="item.id"
        :data="item.data"
        :disabled-actions="Boolean(isProcessingOne && !item.isEdit)"
        :is-edit="item.isEdit"
        :is-cancel-available="true"
        @submit="handleSubmit"
        @edit="handleEdit"
        @delete="handleDelete"
      >
        <template v-if="items.length > 1" v-slot:title><span>Beneficiary Info #{{index + 1}}</span></template>
      </beneficiary-info>
    </div>
    <div v-if="!isProcessingOne && !loading" class="text-center mt-20">
      <dsp-button :is-disabled="isProcessingOne" @click="handleAdd">+ Add Beneficiary Info</dsp-button>
    </div>
    <div v-if="isProcessingOne && !loading" class="beneficiary-save-warning" role="alert">
      <span class="beneficiary-save-warning-icon" aria-hidden="true"></span>
      <span>Please save the changes in <strong>Beneficiary Info</strong> before making a deposit.</span>
    </div>
  </div>
</template>

<script>
import {BillingBeneficiaryService, defaultModel} from "@/services/billingBeneficiaryService";
import BeneficiaryInfo from "@/components/billing/BeneficiaryInfo.vue";
import {cloneDeep} from "lodash";

const service = new BillingBeneficiaryService();

export default {
  name: 'BeneficiaryManager',

  components: {BeneficiaryInfo},

  data() {
    return {
      items: [],
      loading: false
    }
  },

  computed: {
    isProcessingOne() {
      return this.items.some(item => !item.id || item.isEdit)
    }
  },

  watch: {
    items: {
      handler() {
        this.$emit('input', this.items)
      },

      deep: true
    }
  },

  mounted() {
    service.axios = this.$axios;
    this.setBeneficiary();
  },

  methods: {
    setItems(callback) {
      // A feature of vee-validation 2; without this, an error will occur:
      // Error: [vee-validate] Validating a non-existent field: "#[number]". Use "attach()" first.
      const old = cloneDeep(this.items);
      this.items = [];
      this.$nextTick(() => callback(old));
    },

    setBeneficiary() {
      this.loading = true;
      service.all()
        .then(response => {
          if (!response.data.length) {
            this.handleAdd();
            return;
          }

          this.setItems(() => this.items = response.data.map(item => ({...item, isEdit: false})));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @param {BeneficiaryInfo} params
     * @param {number|null} id
     *
     * @return {void}
     */
    handleSubmit(params, id) {
      this.loading = true;
      service.save(params, id)
        .then(response => {
          if (response.id) {
            this.$notify.info('Beneficiary info has been saved successfully');

            const found = this.items.find(item => item.id === id);
            if (!found) {
              return;
            }

            found.id = response.id;
            found.data = response.data;
            this.$set(found, 'stripePublishableKey', response.stripePublishableKey);
            found.isEdit = false;
          }
        })
        .catch(error => {
          if (!error.response || error.response.status !== 422) {
            throw error;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @param {number|null} id
     *
     * @return {void}
     */
    async handleDelete(id) {
      const message = 'Do you want to delete this Beneficiary Info?';
      const answer = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Delete Beneficiary Info',
        okTitle: 'Yes',
        cancelTitle: 'Cancel',
        okVariant: 'danger',
        hideHeaderClose: true,
        centered: true,
        modalClass: 'beneficiary-delete-modal'
      });

      if (!answer) {
        return;
      }

      this.loading = true;
      service.delete(id)
        .then(() => {
          this.$notify.info('Beneficiary info has been deleted successfully');

          const index = this.items.findIndex(item => item.id === id);
          if (index === -1) {
            return;
          }

          this.setItems(old => {
            const result = cloneDeep(old);
            result.splice(index, 1);
            this.items = result;
          });
        })
        .catch(error => {
          if (!error.response || error.response.status !== 422) {
            throw error;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleAdd() {
      this.items.push({id: null, data: defaultModel, isEdit: true})
    },

    /**
     * @param {number|null} id
     * @param {boolean} value
     *
     * @return {void}
     */
    handleEdit(id, value) {
      const index = this.items.findIndex(item => item.id === id);
      if (index === -1) {
        return;
      }

      const found = this.items[index];
      if (!found.id) {
        this.items.splice(index, 1);
        return;
      }

      found.isEdit = value;
    }
  }
}
</script>

<style lang="scss">
@import '@/scss/variables';

.billing-beneficiary-manager .beneficiary-save-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 11px 14px;
  border: 1px solid #FFD666;
  border-radius: 6px;
  background: #FFFBEB;
  color: #D46B08;
  font-size: 12px;
  line-height: 18px;

  .beneficiary-save-warning-icon {
    flex: 0 0 14px;
    width: 14px;
    height: 14px;
    background-color: currentColor;
    -webkit-mask: url('../../images/alert-circle.svg') center / contain no-repeat;
    mask: url('../../images/alert-circle.svg') center / contain no-repeat;
  }
}

.beneficiary-delete-modal {
  .modal-dialog {
    max-width: 350px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 8px;
    box-sizing: content-box;
  }

  .modal-content {
    border: 0;
    border-radius: 6px;
    font-family: Epilogue, sans-serif;
  }

  .modal-header {
    padding: 22px 22px 8px;
    border-bottom: 0;
  }

  .modal-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #212B36;
  }

  .modal-body {
    padding: 0 22px;
    font-size: 12px;
    line-height: 20px;
    color: #637381;
    text-align: left;
  }

  .modal-content .modal-footer {
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 22px 22px;
    border-top: 0;
    box-shadow: none;

    button.btn {
      margin: 0;
      padding: 7px 14px;
      border-radius: 3px;
      font-size: 12px;
      line-height: 18px;
      box-shadow: none;

      &.btn-secondary {
        background: #fff;
        border: 1px solid #C4CDD5;
        color: #637381;

        &:hover,
        &:focus {
          background: #F4F6F8;
        }
      }

      &.btn-danger {
        background: #FF293E;
        border: 1px solid #FF293E;
        color: #fff;
        font-weight: 600;

        &:hover,
        &:focus {
          background: #E52336;
          border-color: #E52336;
        }
      }
    }
  }
}
</style>
